<template>
  <div :class="['cs', tone]">
    <i :class="['mdi', icon, 'cs-ico']" aria-hidden="true"></i>
    <div>
      <p class="cs-main">{{ headline }}</p>
      <p v-for="line in lines" :key="line" class="cs-line">{{ line }}</p>
    </div>
  </div>
</template>

<script>
import { formatDateTime } from '../lib/format';

const DETAILS = [['show_name', 'name'], ['show_title', 'job title'], ['show_company', 'company'], ['show_photo', 'photo']];

export default {
  name: 'ConsentSummary',
  props: {
    consent: { type: Object, default: () => ({}) },
    withdrawnAt: { type: String, default: null },
    origin: { type: String, default: 'form' },
    attestation: { type: Object, default: null },
    sourceLabel: { type: String, default: '' },
    contactEmail: { type: String, default: '' }
  },
  computed: {
    imported() { return this.origin !== 'form'; },
    shown() { return DETAILS.filter(([key]) => this.consent && this.consent[key]).map(([, label]) => label); },
    tone() {
      if (this.withdrawnAt) return 'is-warning';
      return this.consent && this.consent.publish ? 'yes' : 'no';
    },
    icon() {
      if (this.withdrawnAt) return 'mdi-undo-variant';
      return this.consent && this.consent.publish ? 'mdi-check-decagram-outline' : 'mdi-lock-outline';
    },
    headline() {
      if (this.withdrawnAt) return 'Permission withdrawn by the customer on ' + formatDateTime(this.withdrawnAt);
      if (this.imported) return 'Imported · you confirmed permission to publish';
      if (!this.consent || !this.consent.publish) return 'Private · the customer did not allow publishing';
      const parts = ['Allowed to publish'];
      parts.push(this.shown.length ? 'show ' + this.shown.join(', ') : 'without name or photo');
      if (this.consent.given_at) parts.push('given ' + formatDateTime(this.consent.given_at));
      if (this.consent.wording_version) parts.push('wording v' + this.consent.wording_version);
      return parts.join(' · ');
    },
    lines() {
      const out = [];
      if (this.imported) {
        if (this.sourceLabel) out.push('Source: ' + this.sourceLabel);
        if (this.attestation && this.attestation.at) out.push('Confirmed ' + formatDateTime(this.attestation.at) + ': "' + this.attestation.text + '"');
        if (this.shown.length) out.push('Shown with it: ' + this.shown.join(', '));
      }
      if (this.consent && this.consent.details_hidden_at) {
        out.push('Name, job title, company and photo hidden at the customer\'s request on ' + formatDateTime(this.consent.details_hidden_at));
      }
      if (!this.withdrawnAt && !this.imported && this.consent && !this.consent.publish) {
        // There is no reply feature: only the customer's own email address, when they left one.
        out.push('Private feedback can never be published. You can still read it' + (this.contactEmail ? ', reply by email' : '')
          + ' and export it.');
      }
      return out;
    }
  }
};
</script>

<style scoped>
.cs{display:flex;gap:10px;align-items:flex-start;padding:12px 14px;border-radius:12px;border:1px solid #E6DED2;background:#fff}
.cs-ico{font-size:18px;line-height:22px}
.cs.yes{border-color:rgba(47,125,79,.35);background:#F3F9F5}
.cs.yes .cs-ico{color:#2F7D4F}
.cs.no{background:#F3EEE6}
.cs.no .cs-ico{color:#4F4A45}
.cs.is-warning{border-color:rgba(161,92,7,.30);background:#FBF0DF}
.cs.is-warning .cs-ico{color:#A15C07}
.cs-main{margin:0;font-size:14px;line-height:22px;font-weight:600;color:#1F2328}
.cs-line{margin:2px 0 0;font-size:13px;line-height:19px;color:#4F4A45}
</style>
